import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Receipt } from 'lucide-react';
import { Badge } from '../../shared/ui/Badge';
import { Button } from '../../shared/ui/Button';
import { usePageMeta } from '../../shared/hooks/usePageMeta';
import { Skeleton } from '../../shared/ui/Skeleton';
import { EmptyState } from '../../shared/ui/EmptyState';
import { ErrorState } from '../../shared/ui/ErrorState';
import { AnimateIn } from '../../shared/ui/AnimateIn';
import { useMyInvoices } from '../../features/invoices/hooks';
import { fmt } from '../../shared/constants/pricing';
import type { InvoiceStatus } from '../../shared/types/app';

const STATUS_VARIANT: Record<InvoiceStatus, 'success' | 'warning' | 'danger'> = {
  paid: 'success',
  unpaid: 'warning',
  overdue: 'danger',
};

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  paid: 'Paid',
  unpaid: 'Unpaid',
  overdue: 'Overdue',
};

const PAGE_SIZE = 20;

export function InvoicesPage() {
  usePageMeta('My Invoices');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useMyInvoices({ page, limit: PAGE_SIZE });
  const invoices = data?.data ?? [];

  return (
    <div className="flex flex-col">

      {/* Header */}
      <div className="pb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold text-muted uppercase tracking-widest mb-1">Customer</p>
          <h1 className="text-2xl font-black text-text">My Invoices</h1>
        </div>
        <span className="text-xs text-muted">
          {data?.total ?? invoices.length} result{(data?.total ?? invoices.length) !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col gap-2 mt-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-surface rounded-2xl p-4 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-16 rounded-full" />
              </div>
              <Skeleton className="h-3 w-24" />
              <div className="mt-2 pt-3 border-t border-line flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-3 w-3 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="py-12">
          <ErrorState message="Could not load invoices." onRetry={() => refetch()} />
        </div>
      ) : invoices.length === 0 ? (
        <div className="py-20">
          <EmptyState
            title="No invoices yet"
            description="Invoices appear here once a quote is accepted on one of your requests."
            icon={<Receipt size={24} />}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2 mt-5">
          {invoices.map((inv, i) => (
            <AnimateIn key={inv.id} animation="fade-up" delay={i * 60}>
            <Link
              to={`/app/requests/${inv.requestId}`}
              className="group block bg-surface rounded-2xl p-4 hover:bg-surface-raised active:scale-[0.98] transition"
            >
              {/* Top: equipment/job label + status badge */}
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-black text-text leading-snug">{inv.requestLabel || 'Request'}</p>
                <Badge label={STATUS_LABEL[inv.status]} variant={STATUS_VARIANT[inv.status]} className="flex-shrink-0" />
              </div>

              {/* Sub: invoice number · issued date */}
              <p className="text-xs text-muted mt-1">
                <span className="font-mono">#{inv.id.slice(-8).toUpperCase()}</span> · issued{' '}
                {new Date(inv.issuedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>

              {/* Total + due date */}
              <div className="mt-3 pt-3 border-t border-line flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-black text-accent">{fmt(inv.total)}</p>
                  {inv.status !== 'paid' && (
                    <p className="text-xs text-muted mt-0.5">
                      Due {new Date(inv.dueAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                </div>
                <ArrowRight size={13} className="text-muted flex-shrink-0 group-hover:text-accent group-hover:translate-x-0.5 transition" />
              </div>
            </Link>
            </AnimateIn>
          ))}
        </div>
      )}

      {!isLoading && !isError && data && data.totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-line">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="!text-xs normal-case !tracking-normal"
          >
            Previous
          </Button>
          <span className="text-xs text-muted">
            Page {data.page} of {data.totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
            disabled={page >= data.totalPages}
            className="!text-xs normal-case !tracking-normal"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
